/**
 * Create-mode API — calls create-runtime `/api/customer/*` endpoints.
 * why → ADR-005 + SKILL.md §Endpoint Contract (was the retired images-worker
 * `/api/prompts/*` + `/api/images/*` paths that 404 in prod).
 */

import { CONFIG } from '../core/config.js'
import { apiRequest, REQUEST_TIMEOUT_MS } from './index.js'

// Normalize card shape — the create-core schema uses `name`; the inspiration
// renderer expects `id` + `title` (+ `category`, passed through). Without the
// title←name map the style cards render with no title line. why → name/title
// schema drift (same class as the intent-card P0-1).
function normalizeCards(cards) {
  return (cards || []).map((c) => ({ ...c, id: c.id ?? c.card_id, title: c.title ?? c.name }))
}

// The picked image's CARD BUNDLE (ADR-005 #11 — cards follow the image). Loads
// the image's `image_cards` (vision-extracted + persisted on demand for a cold
// image), NOT the flat style library. `idOrCfId` is the library image's numeric
// id or its cloudflare_id.
//
// When the image has NO bundle this returns a "no-bundle" SIGNAL
// (`{ cards: [], has_cards: false }`) — it deliberately does NOT silently fall
// back to the generic style library. The caller owns the fallback: the create
// source slot runs live vision on the image; the inspiration workspace loads the
// flat library. Returning defaults here would make generic cards indistinguishable
// from a real bundle and collapse the bundle-first→vision-fallback ladder to
// "defaults always win". why → ADR-005 2.8.0 #2.
export async function fetchImageCards(idOrCfIdOrUrl) {
  if (!idOrCfIdOrUrl) return { cards: [], has_cards: false, is_default: false }
  // An http(s) URL = an external / injected image with no library row; the
  // endpoint visions it ephemerally. An id/cloudflare_id = a library image.
  const isUrl = typeof idOrCfIdOrUrl === 'string' && /^https?:\/\//.test(idOrCfIdOrUrl)
  try {
    const data = await apiRequest(`${CONFIG.FRONTEND_API}/api/customer/images/bundle`, {
      timeoutMs: REQUEST_TIMEOUT_MS.ANALYSIS,
      method: 'POST',
      body: JSON.stringify(isUrl ? { url: idOrCfIdOrUrl } : { id: idOrCfIdOrUrl }),
    })
    const cards = data.cards || data.data?.cards || []
    if (cards.length > 0) {
      return { cards: normalizeCards(cards), has_cards: true, is_default: false }
    }
  } catch {
    /* no bundle reachable → signal no-bundle, let the caller choose the fallback */
  }
  return { cards: [], has_cards: false, is_default: false }
}

// Generate via create-runtime. The intent card (in card_ids) drives the pipeline
// server-side; the client passes that card's execution_mode as the required
// `intent`. why → ADR-005 #1/#5.
export async function generateFromTemplate({
  intent,
  prompt,
  card_ids,
  reference_asset_id,
  negative_prompt,
  aspect_ratio,
}) {
  return apiRequest(`${CONFIG.FRONTEND_API}/api/customer/generate`, {
    timeoutMs: REQUEST_TIMEOUT_MS.GENERATION,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      intent: intent || 'prompt',
      prompt: prompt || '',
      card_ids: card_ids || [],
      ...(reference_asset_id ? { reference_asset_id } : {}),
      ...(negative_prompt ? { negative_prompt } : {}),
      ...(aspect_ratio ? { aspect_ratio } : {}),
    }),
  })
}

// Auth-scoped history (create-runtime): `limit`+`offset` pagination.
// `customerId` is accepted for caller compatibility but ignored — the server
// scopes to the authenticated customer. why → ADR-005 / SKILL.md §Endpoint Contract.
export async function fetchGenerationHistory(_customerId, limit = 20, offset = 0) {
  const data = await apiRequest(
    `${CONFIG.FRONTEND_API}/api/customer/generate/history?limit=${limit}&offset=${offset}`
  )
  return data.data || []
}

export async function analyzeImageVision(imageUrl) {
  return apiRequest(`${CONFIG.FRONTEND_API}/api/customer/vision/analyze`, {
    timeoutMs: REQUEST_TIMEOUT_MS.ANALYSIS,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ image_url: imageUrl }),
  })
}
